import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useGetFilePaths } from '../../../redux/api/appConfigApi';
import {
  useGetBugReportTypes,
  usePostBugReport,
} from '../../../redux/api/bugReportApi';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { FileVisibility } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IAppFile } from '../../../types/interfaces/app-file';
import { INewFile } from '../../../types/interfaces/new-file';
import {
  deleteFilesFromFirebase,
  uploadFileAndCreateAppFile,
} from '../../../utils/files/firebase-storage-utils';
import { getNewBugReportValidation } from './FormikData';

export function useNewBugReportPage() {
  const [filesToUpload, setFilesToUpload] = useState<INewFile[]>([]);
  const {
    data: types,
    refetch: refetchTypes,
    isLoading: isLoadingTypes,
    isFetching: isFetchingTypes,
    isError: isErrorTypes,
    error: typesError,
  } = useGetBugReportTypes();
  const { t } = useTranslation();
  const [postBugReport] = usePostBugReport();
  const { navigate } = useNavigationStack();
  const { data: user } = useGetMyDetails();
  const {
    data: filePaths,
    refetch: refetchFilePaths,
    isLoading: isLoadingFilePaths,
    isFetching: isFetchingFilePaths,
    isError: isErrorFilePaths,
    error: filePathsError,
  } = useGetFilePaths();
  const [isLoading, setIsLoading] = useState(false);
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getNewBugReportValidation(t),
    initialValues: {
      title: '',
      message: '',
      type: '',
    },
    onSubmit,
  });

  async function onSubmit(values) {
    if (!user || !filePaths || !user.uid || !user.id) {
      return;
    }

    const uploadedStoragePaths: string[] = [];
    let hasError = false;
    setIsLoading(true);
    try {
      const uploadedFiles: IAppFile[] = await Promise.all(
        filesToUpload.map(async (file) => {
          const uploadedFile = await uploadFileAndCreateAppFile(
            file,
            filePaths.bugReportMessages,
            user.uid,
            user.id,
            FileVisibility.public,
          );

          uploadedStoragePaths.push(uploadedFile.storageLink);

          return uploadedFile;
        }),
      );

      const response = await postBugReport({
        body: {
          report: {
            title: values.title,
            typeId: values.type.id,
          },
          message: values.message,
          filesToUpload: uploadedFiles,
        },
      }).unwrap();

      navigate(Paths.bugReports.toId(response.id), {
        replace: true,
      });
    } catch (error) {
      hasError = true;
      console.error(
        'Error uploading files to Firebase Storage or creating bug report:',
        error,
      );
      errorToastMessage(t('messages.uploadOrSaveFailed'));
    } finally {
      setIsLoading(false);

      if (hasError && uploadedStoragePaths.length > 0) {
        await deleteFilesFromFirebase(uploadedStoragePaths);
      }
    }
  }

  function onFilesToUploadChanged(files: INewFile[]) {
    setFilesToUpload(files);
  }

  return {
    formik,
    types,
    filePaths,
    isLoading,
    onFilesToUploadChanged,
    refetchTypes,
    refetchFilePaths,
    isLoadingTypes,
    isFetchingTypes,
    isErrorTypes,
    typesError,
    isLoadingFilePaths,
    isFetchingFilePaths,
    isErrorFilePaths,
    filePathsError,
  };
}
